import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/authContext.ts";
import {
  apiConfigurada,
  configurarAlertaAviso,
  avisosPorInforme,
  capturarAcuseInforme,
  crearInformeModificatorio,
  generarXmlInforme,
  listarAvisosInforme,
  listarCatalogo,
  listarInformes,
  obtenerXmlInforme,
  presentarInforme,
  procedeModificatorio,
  subirAcuseInforme,
  validarInforme,
  type AvisoInformeRow,
  type EstadoInforme,
  type InformeRow,
} from "../../lib/api.ts";
import { C, fecha, st } from "../../ui/theme.ts";
import { IconoReporte } from "../../ui/iconos.tsx";

/**
 * REPORTES — rebanada 2 de la beta. La cola de informes DIN (E6): un informe por
 * mes (grano oficial), en ceros cuando no hubo operaciones, y el ciclo
 * borrador → generado → validado → presentado → acuse capturado.
 * "Presentado" NO es cumplido: el ciclo cierra con el acuse. La e.firma es
 * monitoreo (0019): si no consta vigente, se ADVIERTE al presentar, sin bloquear.
 * Nota técnica: el enum legacy de BD conserva estado='firmado'; en UI se muestra
 * como validado porque Argus no firma ni custodia FIEL.
 */

const OPACIDAD_DESHABILITADO = 0.6;
const LEN_ANIO = 4;
const XML_HASH_LEN = 64;
const HASH_PREVIEW_LEN = 12;
const FOLIO_ACUSE_RE = /^[0-9]{4}-[1-9][0-9]{0,8}$/;
const FW_HECHO = 700;
const FW_PENDIENTE = 500;
const DEMO_AVISOS: Record<string, number> = { "dem-1": 3, "dem-2": 5 };
const ALERTA_SIN_ALERTA = "100";
const PRIORIDAD_NORMAL = 1;
const PRIORIDAD_24H = 2;
type CatalogoOption = [string, string];

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function mesBonito(mes: string): string {
  const anio = mes.slice(0, LEN_ANIO);
  const idx = Number(mes.slice(LEN_ANIO)) - 1;
  const nombre = MESES[idx] ?? mes;
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${anio}`;
}

const PASOS: [string, string][] = [
  ["borrador", "Borrador"],
  ["generado", "XML generado"],
  ["firmado", "Paquete validado"],
  ["presentado", "Presentado en SPPLD"],
  ["acuse_capturado", "Acuse capturado"],
];

function ChipEstado({ estado }: { estado: string }) {
  const m: Record<string, { c: string; bg: string; t: string }> = {
    borrador: { c: C.muted, bg: C.grayBg, t: "Borrador" },
    generado: { c: C.blue, bg: C.blueBg, t: "XML listo — falta validar" },
    firmado: { c: C.blue, bg: C.blueBg, t: "Validado — falta presentar" },
    presentado: { c: C.amber, bg: C.amberBg, t: "Presentado — SIN acuse" },
    acuse_capturado: { c: C.green, bg: C.greenBg, t: "Cumplido — acuse ✓" },
    cancelado: { c: C.muted, bg: C.grayBg, t: "Cancelado" },
  };
  const s = m[estado] ?? { c: C.muted, bg: C.grayBg, t: estado };
  return (
    <span style={{ display: "inline-flex", padding: "2px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 600, color: s.c, background: s.bg, whiteSpace: "nowrap" }}>
      {s.t}
    </span>
  );
}

const DEMO_XML = "<archivo><!-- XML DIN de demostración --></archivo>";

// Meses de demo CALCULADOS de la fecha actual: la demo siempre muestra meses
// recientes y no hay claves AAAAMM literales (el gate cero-pesos del CI vigila
// cadenas de 6+ dígitos en el código).
const DIEZ = 10;
const MESES_DEMO_N = 3;
const DIA_LIMITE = 17;
const LEN_FECHA_ISO = 10;
const HORAS_POR_DIA = 24;
const MINUTOS_POR_HORA = 60;
const SEGUNDOS_POR_MINUTO = 60;
const MS_POR_SEGUNDO = 1000;
const MS_POR_DIA = MS_POR_SEGUNDO * SEGUNDOS_POR_MINUTO * MINUTOS_POR_HORA * HORAS_POR_DIA;
const DIAS_FUERA_VENTANA_MODIFICATORIO = 60;

/** ISO de hace `nDias` días — para que el modificatorio demo se muestre fuera de ventana. */
function hace(nDias: number): string {
  return new Date(Date.now() - nDias * MS_POR_DIA).toISOString();
}

function mesAtras(n: number): Date {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return d;
}

function claveMes(d: Date): string {
  const m = d.getMonth() + 1;
  return `${String(d.getFullYear())}${m < DIEZ ? "0" : ""}${String(m)}`;
}

/** Día N del mes SIGUIENTE al de `d`, como fecha ISO (regla del día 17). */
function diaMesSiguiente(d: Date, dia: number): string {
  const s = new Date(d.getFullYear(), d.getMonth() + 1, dia);
  return s.toISOString().slice(0, LEN_FECHA_ISO);
}

function informesDemo(): InformeRow[] {
  const [m1, m2, m3] = Array.from({ length: MESES_DEMO_N }, (_, i) => mesAtras(i + 1));
  if (!m1 || !m2 || !m3) return [];
  return [
    { id: "dem-1", mes_reportado: claveMes(m1), formato_codigo: "ANEXO_5B", estado: "generado", es_en_ceros: false, vence_en: diaMesSiguiente(m1, DIA_LIMITE), xml_sha256: null, folio_acuse: null, acuse_documento_id: null, presentado_en: null, acuse_en: null, modificatorio_de: null },
    { id: "dem-2", mes_reportado: claveMes(m2), formato_codigo: "INMUEBLES_XML", estado: "acuse_capturado", es_en_ceros: false, vence_en: diaMesSiguiente(m2, DIA_LIMITE), xml_sha256: "0123456789abcdef0123456789abcdef0123456789abcdef0123456789abcdef", folio_acuse: "2026-15951", acuse_documento_id: "demo-acuse-2", presentado_en: diaMesSiguiente(m2, DIA_LIMITE - 1), acuse_en: new Date().toISOString(), modificatorio_de: null },
    { id: "dem-3", mes_reportado: claveMes(m3), formato_codigo: "ANEXO_5B", estado: "acuse_capturado", es_en_ceros: true, vence_en: diaMesSiguiente(m3, DIA_LIMITE), xml_sha256: "abcdef0123456789abcdef0123456789abcdef0123456789abcdef0123456789", folio_acuse: "2026-14802", acuse_documento_id: "demo-acuse-3", presentado_en: diaMesSiguiente(m3, DIA_LIMITE - 1), acuse_en: hace(DIAS_FUERA_VENTANA_MODIFICATORIO), modificatorio_de: null },
  ];
}

// Etiqueta corta del formato del informe (canal oficial ante el SPPLD).
const FORMATO_LABEL: Record<string, string> = {
  ANEXO_5B: "DIN",
  INMUEBLES_XML: "INM",
  ARRENDAMIENTO_XML: "ARI",
};

function formatoEtiqueta(codigo: string): string {
  return FORMATO_LABEL[codigo] ?? codigo;
}

function opcionesCatalogo(rows: { clave: string; nombre: string }[]): CatalogoOption[] {
  return rows.map((r) => [r.clave, r.nombre]);
}

function ChipFormato({ codigo }: { codigo: string }) {
  const esInm = codigo === "INMUEBLES_XML";
  const esAri = codigo === "ARRENDAMIENTO_XML";
  const titulo = esInm
    ? "Inmuebles (Art. 17 fr. V)"
    : esAri
      ? "Arrendamiento de inmuebles (Art. 17 fr. XV)"
      : "Desarrollo inmobiliario / DIN (Art. 17 fr. V Bis)";
  return (
    <span
      title={titulo}
      style={{
        display: "inline-flex",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.04em",
        color: esInm ? C.blue : esAri ? C.amber : C.tielDark,
        background: esInm ? C.blueBg : esAri ? C.amberBg : C.tielSoft,
        whiteSpace: "nowrap",
      }}
    >
      {formatoEtiqueta(codigo)}
    </span>
  );
}

export function ReportesView() {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const tenantId = session?.tenantId ?? "";
  const demo = !apiConfigurada() || token === "";

  const [informes, setInformes] = useState<InformeRow[]>(demo ? informesDemo() : []);
  const [conteos, setConteos] = useState<Map<string, number>>(new Map(Object.entries(DEMO_AVISOS)));
  const [selId, setSelId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [avisoOk, setAvisoOk] = useState<string | null>(null);
  const [cargando, setCargando] = useState(!demo);
  const [ocupado, setOcupado] = useState(false);
  const [folio, setFolio] = useState("");
  const [archivoAcuse, setArchivoAcuse] = useState<File | null>(null);
  const [descripcionModificacion, setDescripcionModificacion] = useState("");
  const [avisosDin, setAvisosDin] = useState<AvisoInformeRow[]>([]);
  const [alertasDin, setAlertasDin] = useState<CatalogoOption[]>([[ALERTA_SIN_ALERTA, "Sin alerta."], ["9999", "Otra alerta."]]);
  const [alertaDrafts, setAlertaDrafts] = useState<Record<string, { prioridad: string; tipo_alerta: string; descripcion_alerta: string }>>({});

  const sel = informes.find((i) => i.id === selId) ?? null;

  const recargar = useCallback(async () => {
    if (demo) return;
    const [inf, cnt] = await Promise.all([listarInformes(token), avisosPorInforme(token)]);
    setInformes(inf);
    setConteos(cnt);
  }, [demo, token]);

  useEffect(() => {
    if (demo) return;
    setCargando(true);
    recargar()
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar los informes."); })
      .finally(() => { setCargando(false); });
  }, [demo, recargar]);

  const recargarAvisos = useCallback(async () => {
    if (demo || !selId) {
      setAvisosDin([]);
      return;
    }
    const [avs, alertas] = await Promise.all([
      listarAvisosInforme(token, selId),
      listarCatalogo(token, "cat_tipo_alerta"),
    ]);
    setAvisosDin(avs);
    setAlertasDin(opcionesCatalogo(alertas));
  }, [demo, selId, token]);

  useEffect(() => {
    void recargarAvisos().catch((e: unknown) => {
      setError(e instanceof Error ? e.message : "No se pudieron cargar los avisos del informe.");
    });
  }, [recargarAvisos]);

  const accion = async (fn: () => Promise<void>) => {
    setError(null);
    setAvisoOk(null);
    setOcupado(true);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "La acción falló.");
    } finally {
      setOcupado(false);
    }
  };

  const transicionDemo = (id: string, estado: EstadoInforme, extra?: Partial<InformeRow>) => {
    setInformes((xs) => xs.map((i) => (i.id === id ? { ...i, estado, ...extra } : i)));
  };

  const alertaDraftDe = (a: AvisoInformeRow) =>
    alertaDrafts[a.id] ?? {
      prioridad: String(a.prioridad),
      tipo_alerta: a.tipo_alerta,
      descripcion_alerta: a.descripcion_alerta ?? "",
    };

  const guardarAlertaAviso = async (aviso: AvisoInformeRow) => {
    const draft = alertaDraftDe(aviso);
    const prioridad = Number(draft.prioridad);
    const tipoAlerta = draft.tipo_alerta;
    const descripcion = draft.descripcion_alerta.trim();
    await accion(async () => {
      if (prioridad === PRIORIDAD_NORMAL && tipoAlerta !== ALERTA_SIN_ALERTA) {
        throw new Error("La prioridad normal debe quedar sin alerta.");
      }
      if (prioridad === PRIORIDAD_24H && tipoAlerta === ALERTA_SIN_ALERTA) {
        throw new Error("La prioridad 24 horas requiere seleccionar una alerta.");
      }
      if (tipoAlerta === "9999" && descripcion.length === 0) {
        throw new Error("Describe la alerta cuando selecciones otra alerta.");
      }
      await configurarAlertaAviso(token, aviso.id, prioridad, tipoAlerta, descripcion || null);
      await Promise.all([recargar(), recargarAvisos()]);
      setAvisoOk(prioridad === PRIORIDAD_24H
        ? "Alerta DIN guardada. Se abrió el reloj de 24 horas y el XML deberá regenerarse."
        : "Alerta DIN normalizada. El reloj de 24 horas quedó cancelado si existía.");
    });
  };

  const descargarXml = async (inf: InformeRow) => {
    const xml = demo ? DEMO_XML : await obtenerXmlInforme(token, inf.id);
    if (!xml) {
      setError("El informe aún no tiene XML generado.");
      return;
    }
    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `informe-${formatoEtiqueta(inf.formato_codigo).toLowerCase()}-${inf.mes_reportado}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="argus-master-detail" style={{ display: "flex", flex: 1, minWidth: 0 }}>
      {/* Lista central */}
      <section className="argus-list-pane" style={{ width: 330, flexShrink: 0, borderRight: `1px solid ${C.line}`, display: "flex", flexDirection: "column", background: "#FBFCFB" }}>
        <div style={{ padding: "16px 16px 10px" }}>
          <h2 style={{ ...st.display, margin: 0, fontSize: 18 }}>Reportes</h2>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>
            Un informe por mes. Los avisos nacen de la captura; no está cumplido hasta el acuse.
          </p>
        </div>
        {demo && (
          <div style={{ margin: "8px 16px", padding: "6px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 11.5, fontWeight: 600 }}>
            Modo demostración — el ciclo completo funciona en memoria.
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: "6px 12px 8px" }}>
          {cargando && <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Cargando…</p>}
          {!cargando && informes.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>
              Sin informes aún — se crean solos al capturar una operación reportable o con el cierre de mes (en ceros).
            </p>
          )}
          {informes.map((i) => {
            const activa = selId === i.id;
            const n = conteos.get(i.id) ?? 0;
            return (
              <button
                key={i.id}
                onClick={() => { setSelId(i.id); setError(null); setAvisoOk(null); setFolio(""); setArchivoAcuse(null); }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${activa ? C.tiel : C.line}`,
                  boxShadow: activa ? `0 0 0 1px ${C.tiel}` : "none",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <ChipFormato codigo={i.formato_codigo} />
                    <span style={{ fontWeight: 700, fontSize: 13.5, color: C.ink }}>{mesBonito(i.mes_reportado)}</span>
                  </span>
                  <ChipEstado estado={i.estado} />
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                  {i.es_en_ceros ? "En ceros (sin operaciones)" : `${String(n)} aviso${n === 1 ? "" : "s"}`}
                  {i.vence_en ? ` · vence ${fecha(i.vence_en)}` : ""}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Detalle */}
      <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
        {error && (
          <div style={{ maxWidth: 560, margin: "0 auto 12px", padding: "10px 14px", borderRadius: 10, background: C.redBg, color: C.red, fontSize: 13 }}>
            {error}
          </div>
        )}
        {avisoOk && (
          <div style={{ maxWidth: 560, margin: "0 auto 12px", padding: "10px 14px", borderRadius: 10, background: C.amberBg, color: C.amber, fontSize: 13 }}>
            {avisoOk}
          </div>
        )}
        {sel ? (
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 2px", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
              <ChipFormato codigo={sel.formato_codigo} />
              <span>Informe · {mesBonito(sel.mes_reportado)}</span>
            </h2>
            <p style={{ margin: "0 0 10px", color: C.muted, fontSize: 13 }}>
              {sel.es_en_ceros ? "En ceros — se presenta igual para acreditar el mes." : `${String(conteos.get(sel.id) ?? 0)} avisos incluidos.`}
              {sel.vence_en ? ` Fecha límite: ${fecha(sel.vence_en)} (día 17 + facilidad por RFC).` : ""}
            </p>
            <div style={{ marginBottom: 14 }}><ChipEstado estado={sel.estado} /></div>

            <div style={st.tarjeta}>
              <p style={st.eyebrow}>Ciclo del informe</p>
              {PASOS.map(([clave, etiqueta], idx) => {
                const idxActual = PASOS.findIndex(([k]) => k === sel.estado);
                const hecho = idxActual >= idx;
                return (
                  <div key={clave} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0" }}>
                    <span
                      aria-hidden
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        color: "#fff",
                        background: hecho ? C.green : "#D6DBDA",
                      }}
                    >
                      {hecho ? "✓" : ""}
                    </span>
                    <span style={{ fontSize: 13.5, fontWeight: hecho ? FW_HECHO : FW_PENDIENTE, color: hecho ? C.ink : C.muted }}>{etiqueta}</span>
                  </div>
                );
              })}
              {sel.folio_acuse && (
                <p style={{ margin: "8px 0 0", fontSize: 12.5, color: C.muted }}>
                  Folio de acuse: <b style={{ color: C.ink }}>{sel.folio_acuse}</b>
                </p>
              )}
            </div>

            {sel.formato_codigo === "ANEXO_5B" && !sel.es_en_ceros && avisosDin.length > 0 && (
              <div style={st.tarjeta}>
                <p style={st.eyebrow}>Alertas de avisos DIN</p>
                {avisosDin.map((aviso) => {
                  const draft = alertaDraftDe(aviso);
                  const prioridad24h = draft.prioridad === "2";
                  const bloqueado = ocupado || sel.estado === "presentado" || sel.estado === "acuse_capturado" || sel.estado === "cancelado";
                  const alertaInvalida =
                    (draft.prioridad === "1" && draft.tipo_alerta !== ALERTA_SIN_ALERTA) ||
                    (prioridad24h && draft.tipo_alerta === ALERTA_SIN_ALERTA) ||
                    (draft.tipo_alerta === "9999" && draft.descripcion_alerta.trim().length === 0);
                  return (
                    <div key={aviso.id} style={{ borderTop: `1px solid ${C.line}`, paddingTop: 10, marginTop: 10 }}>
                      <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 8 }}>
                        {aviso.referencia_aviso ?? aviso.id.slice(0, HASH_PREVIEW_LEN)} · {aviso.modalidad}
                      </div>
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(88px, 0.6fr) minmax(160px, 1.4fr)",
                        gap: 8,
                        alignItems: "center",
                      }}>
                        <select
                          value={draft.prioridad}
                          disabled={bloqueado}
                          onChange={(e) => {
                            const prioridad = e.target.value;
                            setAlertaDrafts((actual) => ({
                              ...actual,
                              [aviso.id]: {
                                ...draft,
                                prioridad,
                                tipo_alerta: prioridad === "1" ? ALERTA_SIN_ALERTA : draft.tipo_alerta,
                                descripcion_alerta: prioridad === "1" ? "" : draft.descripcion_alerta,
                              },
                            }));
                          }}
                          style={st.input}
                        >
                          <option value="1">Normal</option>
                          <option value="2">24 horas</option>
                        </select>
                        <select
                          value={draft.tipo_alerta}
                          disabled={bloqueado || draft.prioridad === "1"}
                          onChange={(e) => {
                            const tipoAlerta = e.target.value;
                            setAlertaDrafts((actual) => ({
                              ...actual,
                              [aviso.id]: {
                                ...draft,
                                tipo_alerta: tipoAlerta,
                                descripcion_alerta: tipoAlerta === ALERTA_SIN_ALERTA ? "" : draft.descripcion_alerta,
                              },
                            }));
                          }}
                          style={st.input}
                        >
                          {alertasDin.map(([clave, nombre]) => (
                            <option key={clave} value={clave}>{clave} — {nombre}</option>
                          ))}
                        </select>
                        {prioridad24h && (
                          <input
                            value={draft.descripcion_alerta}
                            disabled={bloqueado}
                            onChange={(e) => {
                              const valor = e.target.value;
                              setAlertaDrafts((actual) => ({
                                ...actual,
                                [aviso.id]: { ...draft, descripcion_alerta: valor },
                              }));
                            }}
                            placeholder="Descripción de la alerta"
                            style={{ ...st.input, gridColumn: "1 / -1" }}
                          />
                        )}
                        <button
                          style={{ ...st.btn, gridColumn: "1 / -1", opacity: bloqueado || alertaInvalida ? OPACIDAD_DESHABILITADO : 1 }}
                          disabled={bloqueado || alertaInvalida}
                          onClick={() => { void guardarAlertaAviso(aviso); }}
                        >
                          Guardar alerta DIN
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={st.tarjeta}>
              <p style={st.eyebrow}>Tu siguiente paso</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(sel.estado === "borrador" || sel.estado === "generado") && (
                  <button
                    style={{ ...st.btnPrim, opacity: ocupado ? OPACIDAD_DESHABILITADO : 1 }}
                    disabled={ocupado}
                    onClick={() => {
                      void accion(async () => {
                        if (demo) {
                          transicionDemo(sel.id, "generado");
                        } else {
                          await generarXmlInforme(token, sel.id, sel.formato_codigo);
                          await recargar();
                        }
                      });
                    }}
                  >
                    {sel.estado === "borrador" ? "Generar el XML del informe" : "Regenerar XML"}
                  </button>
                )}
                {sel.estado !== "borrador" && sel.estado !== "cancelado" && (
                  <button style={st.btn} disabled={ocupado} onClick={() => { void accion(() => descargarXml(sel)); }}>
                    Descargar XML
                  </button>
                )}
                {sel.estado === "generado" && (
                  <button
                    style={st.btn}
                    disabled={ocupado}
                    onClick={() => {
                      void accion(async () => {
                        if (demo) {
                          transicionDemo(sel.id, "firmado");
                        } else {
                          await validarInforme(token, sel.id);
                          await recargar();
                        }
                      });
                    }}
                  >
                    Validar paquete
                  </button>
                )}
                {sel.estado === "firmado" && (
                  <button
                    style={st.btn}
                    disabled={ocupado}
                    onClick={() => {
                      void accion(async () => {
                        if (demo) {
                          transicionDemo(sel.id, "presentado", {
                            presentado_en: new Date().toISOString(),
                            xml_sha256: "f".repeat(XML_HASH_LEN),
                          });
                          setAvisoOk("Presentado. Recuerda: no está cumplido hasta guardar el acuse.");
                        } else {
                          const r = await presentarInforme(token, sel.id);
                          if (r.advertencia_efirma) setAvisoOk(r.advertencia_efirma);
                          await recargar();
                        }
                      });
                    }}
                  >
                    Marcar presentado en el portal SPPLD
                  </button>
                )}
                {sel.estado === "presentado" && (
                  <div>
                    <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Folio del acuse (AAAA-1..9 dígitos)
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                      <input
                        style={st.input}
                        placeholder="2026-123"
                        value={folio}
                        onChange={(e) => { setFolio(e.target.value); }}
                      />
                      <input
                        type="file"
                        accept=".pdf,.xml,image/*"
                        onChange={(e) => { setArchivoAcuse(e.target.files?.[0] ?? null); }}
                        style={st.input}
                      />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: C.muted }}>
                          {archivoAcuse ? `Acuse: ${archivoAcuse.name}` : "Adjunta el acuse descargado del SPPLD."}
                        </span>
                        <button
                          style={{ ...st.btnPrim, whiteSpace: "nowrap", opacity: ocupado ? OPACIDAD_DESHABILITADO : 1 }}
                          disabled={ocupado || !FOLIO_ACUSE_RE.test(folio.trim()) || (!demo && !archivoAcuse)}
                          onClick={() => {
                            void accion(async () => {
                              const folioLimpio = folio.trim();
                              if (demo) {
                                transicionDemo(sel.id, "acuse_capturado", { folio_acuse: folioLimpio, acuse_documento_id: "demo-acuse", acuse_en: new Date().toISOString() });
                              } else {
                                if (!archivoAcuse) throw new Error("Adjunta el archivo del acuse descargado del SPPLD.");
                                const documentoId = await subirAcuseInforme({ token, tenantId, informeId: sel.id, archivo: archivoAcuse });
                                await capturarAcuseInforme(token, sel.id, folioLimpio, documentoId);
                                setArchivoAcuse(null);
                                await recargar();
                              }
                            });
                          }}
                        >
                          Guardar acuse
                        </button>
                      </div>
                    </div>
                    {sel.xml_sha256 && (
                      <p style={{ margin: "8px 0 0", fontSize: 12, color: C.muted }}>
                        XML presentado: <b>{sel.xml_sha256.slice(0, HASH_PREVIEW_LEN)}…</b>
                      </p>
                    )}
                  </div>
                )}
                {sel.estado === "acuse_capturado" && (
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: C.greenBg, color: C.green, fontSize: 13.5, fontWeight: 600 }}>
                    ✓ Mes cerrado: presentado y con acuse guardado. Todo quedó sellado en el expediente probatorio.
                  </div>
                )}
                {sel.estado === "acuse_capturado" && procedeModificatorio(sel, informes) && (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Descripción de la corrección (aviso modificatorio, Art. 8)
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                      <input
                        style={st.input}
                        placeholder="Qué se corrige y por qué"
                        value={descripcionModificacion}
                        onChange={(e) => { setDescripcionModificacion(e.target.value); }}
                      />
                      <button
                        style={{ ...st.btn, opacity: ocupado || descripcionModificacion.trim() === "" ? OPACIDAD_DESHABILITADO : 1 }}
                        disabled={ocupado || descripcionModificacion.trim() === ""}
                        onClick={() => {
                          void accion(async () => {
                            if (demo) {
                              setAvisoOk("Modo demostración: el modificatorio se crearía en borrador ligado a este informe.");
                            } else {
                              await crearInformeModificatorio(token, sel.id, descripcionModificacion.trim());
                              setDescripcionModificacion("");
                              await recargar();
                            }
                          });
                        }}
                      >
                        📝 Crear aviso modificatorio
                      </button>
                      <p style={{ margin: 0, fontSize: 11.5, color: C.muted }}>
                        Disponible dentro de los 30 días del acuse; una sola vez por informe (Art. 8).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ ...st.tarjeta, background: C.tielSoft, borderColor: "#C8E0DD" }}>
              <p style={{ ...st.eyebrow, color: C.tielDark }}>Cómo funciona</p>
              <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.7 }}>
                El XML se arma desde las operaciones capturadas; el serializador está cubierto
                por prueba golden contra el XSD oficial. La firma real ocurre en el portal SPPLD
                con TU e.firma — Argus nunca la custodia; al presentar se guarda el hash del XML
                y el cierre exige acuse documental en bóveda.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: 420, margin: "18vh auto 0", textAlign: "center", color: C.muted }}>
            <div style={{ marginBottom: 8, color: C.tielDark }}><IconoReporte size={34} /></div>
            <h3 style={{ margin: "0 0 6px", color: C.ink }}>Selecciona un informe</h3>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              El informe mensual es el grano oficial ante la UIF. De aquí sale el XML, aquí se
              valida el paquete, se registra la presentación y aquí se guarda el acuse que cierra el mes.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
